import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import emailjs from '@emailjs/browser';

// ── Data ──────────────────────────────────────────────────────────────────────

const services = [
  {
    id: 'development',
    number: '01',
    icon: '🚀',
    title: 'Custom Software Development',
    shortDesc: 'End-to-end product development from architecture to deployment.',
    fullDesc: 'From zero-to-one MVP builds to enterprise-scale systems. We bring the architecture discipline, code quality, and product thinking to build software that lasts. React, Node, Python, Laravel, React Native — we match the stack to your problem, not the other way around.',
    deliverables: [
      'MVP Development (8-12 weeks)',
      'Enterprise Web Applications',
      'Mobile Apps (iOS & Android)',
      'API Design & Microservices',
      'Cloud Migration & Scaling',
    ],
    color: '#3B82F6',
  },
  {
    id: 'augmentation',
    number: '02',
    icon: '👥',
    title: 'Staff Augmentation',
    shortDesc: 'Pre-vetted engineers embedded in your team within 48 hours.',
    fullDesc: 'Skip the 3-month recruitment cycle. We provide senior developers, AI engineers, and DevOps specialists who embed directly into your team. You manage the roadmap; we provide the execution horsepower. Perfect for teams that need to ship faster without the hiring overhead.',
    deliverables: [
      'Backend & Frontend Engineers',
      'Mobile Developers (React Native/Flutter)',
      'AI & Data Engineers',
      'DevOps & Cloud Architects',
      'UX/UI Designers',
    ],
    color: '#8B5CF6',
  },
  {
    id: 'ai',
    number: '03',
    icon: '🤖',
    title: 'AI & LLM Integration',
    shortDesc: 'Production-ready AI systems beyond the prototype stage.',
    fullDesc: 'We build AI systems that actually work in production — not just demos. From LLM integration and RAG pipelines to computer vision and predictive analytics. We understand the difference between a cool prototype and a system that handles real user load at scale.',
    deliverables: [
      'LLM Integration (OpenAI, Claude, Llama)',
      'Retrieval-Augmented Generation (RAG)',
      'Computer Vision Systems',
      'Predictive Analytics',
      'MLOps & Model Deployment',
    ],
    color: '#06B6D4',
  },
  {
    id: 'design',
    number: '04',
    icon: '🎨',
    title: 'UX/UI & Product Design',
    shortDesc: 'Interfaces that convert, backed by research not assumptions.',
    fullDesc: 'We design interfaces that are intuitive, beautiful, and conversion-focused. From comprehensive design systems to user research and testing — our design process eliminates guesswork and validates with real users before a single line of code is written.',
    deliverables: [
      'Product Design (Figma)',
      'Design Systems',
      'UX Research & Testing',
      'Prototyping & Motion',
      'Design-to-Code Handoff',
    ],
    color: '#EC4899',
  },
];

const processSteps = [
  {
    step: '01',
    title: 'Discovery & Architecture',
    desc: 'We start with your business problem, not a tech stack. We map user journeys, define system architecture, and identify the 20% of features that drive 80% of value.',
  },
  {
    step: '02',
    title: 'Rapid Prototyping',
    desc: 'Working prototypes within 2-3 weeks. You see and feel the product before we build the full system. Iterate early, avoid expensive mistakes later.',
  },
  {
    step: '03',
    title: 'Agile Development',
    desc: 'Two-week sprints with working demos. Transparent progress tracking. Direct access to the engineers building your product. No black boxes.',
  },
  {
    step: '04',
    title: 'Launch & Scale',
    desc: 'Production deployment is just the beginning. We monitor, optimise, and scale your systems. Long-term retainers available for evolving products.',
  },
];

const techStack = [
  { category: 'Frontend', items: ['React.js', 'Next.js', 'Vue.js', 'TypeScript', 'React Native', 'Flutter'] },
  { category: 'Backend', items: ['Node.js', 'Python/Django', 'PHP/Laravel', 'Java Spring', 'Go', 'Rust'] },
  { category: 'AI & Data', items: ['OpenAI API', 'LangChain', 'TensorFlow', 'PyTorch', 'Vector DBs', 'MLOps'] },
  { category: 'Cloud & DevOps', items: ['AWS', 'Google Cloud', 'Firebase', 'Docker', 'Kubernetes', 'CI/CD'] },
];

const whyUs = [
  {
    icon: '🏗️',
    title: 'Systems Thinking',
    desc: 'We do not just write code. We design systems that compound in value over time — maintainable, scalable, and documented.',
  },
  {
    icon: '⚡',
    title: 'Speed Without Sacrifice',
    desc: 'We move fast because we have built similar systems before. Reusable architecture patterns mean quicker delivery without technical debt.',
  },
  {
    icon: '🔒',
    title: 'Security First',
    desc: 'AES-256 encryption, DPDP Act compliance, zero-trust architecture — baked in from day one, not retrofitted later.',
  },
  {
    icon: '🇮🇳',
    title: 'India Cost, Global Quality',
    desc: 'Dehradun-based team with Silicon Valley engineering standards. Timezone-friendly for US and EU clients.',
  },
];

const engagementModels = [
  {
    name: 'Fixed Scope',
    desc: 'Defined requirements, fixed timeline, fixed budget. Best for MVPs and well-defined features.',
    ideal: 'Startups, new product launches',
  },
  {
    name: 'Dedicated Team',
    desc: 'Full-time engineers embedded in your team. Monthly retainer. Best for long-term product development.',
    ideal: 'Scale-ups, enterprise innovation teams',
  },
  {
    name: 'Time & Material',
    desc: 'Pay for hours worked. Maximum flexibility. Best for R&D, AI experiments, and evolving requirements.',
    ideal: 'AI projects, complex integrations',
  },
];

// ── Project Form ──────────────────────────────────────────────────────────────

function ProjectForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: 'Custom Software Development',
    budget: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await emailjs.send(
        'service_g9tvszg',
        'template_124o1la',
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone || 'Not provided',
          company: formData.company || 'Not provided',
          service_interest: formData.service,
          budget: formData.budget || 'Not specified',
          message: formData.message,
          to_name: 'Ashish Uniyal',
        },
        'u6gb-CCFGNTwlW3Ot'
      );
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please email us directly at a.uniyal@redcubicalsystems.com');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(59,130,246,0.2)',
    borderRadius: '10px',
    padding: '14px 16px',
    fontSize: '14px',
    color: '#fff',
    fontFamily: 'Inter, sans-serif',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: '700',
    color: 'rgba(255,255,255,0.4)',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: '6px',
  };

  if (submitted) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '48px 32px',
        background: 'rgba(59,130,246,0.06)',
        border: '1px solid rgba(59,130,246,0.2)',
        borderRadius: '16px',
      }}>
        <p style={{ fontSize: '48px', marginBottom: '16px' }}>🚀</p>
        <h3 style={{
          fontFamily: 'Barlow Condensed, sans-serif',
          fontSize: '26px', fontWeight: '900',
          color: '#fff', textTransform: 'uppercase',
          marginBottom: '10px',
        }}>
          Message Received!
        </h3>
        <p style={{
          fontSize: '14px',
          color: 'rgba(255,255,255,0.5)',
          lineHeight: '1.7',
        }}>
          Ashish will personally review your project requirements
          and respond within 24 hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div className="services-form-row">
        <div>
          <label style={labelStyle}>Your Name *</label>
          <input
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Full name"
            required
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
        <div>
          <label style={labelStyle}>Email *</label>
          <input
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@company.com"
            required
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
      </div>

      <div className="services-form-row">
        <div>
          <label style={labelStyle}>Phone</label>
          <input
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+91 98765 43210"
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
        <div>
          <label style={labelStyle}>Company</label>
          <input
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder="Your company name"
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
      </div>

      <div className="services-form-row">
        <div>
          <label style={labelStyle}>Service Interest</label>
          <select
            name="service"
            value={formData.service}
            onChange={handleChange}
            style={{
              ...inputStyle,
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236B7280' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 16px center',
              paddingRight: '40px',
            }}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          >
            <option value="Custom Software Development" style={{ background: '#0A0E1A' }}>Custom Software Development</option>
            <option value="Staff Augmentation" style={{ background: '#0A0E1A' }}>Staff Augmentation</option>
            <option value="AI & LLM Integration" style={{ background: '#0A0E1A' }}>AI & LLM Integration</option>
            <option value="UX/UI Design" style={{ background: '#0A0E1A' }}>UX/UI Design</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Budget Range (Optional)</label>
          <select
            name="budget"
            value={formData.budget}
            onChange={handleChange}
            style={{
              ...inputStyle,
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236B7280' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 16px center',
              paddingRight: '40px',
            }}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          >
            <option value="" style={{ background: '#0A0E1A' }}>Select budget range</option>
            <option value="₹5L - ₹15L" style={{ background: '#0A0E1A' }}>₹5L - ₹15L</option>
            <option value="₹15L - ₹50L" style={{ background: '#0A0E1A' }}>₹15L - ₹50L</option>
            <option value="₹50L - ₹1Cr" style={{ background: '#0A0E1A' }}>₹50L - ₹1Cr</option>
            <option value="₹1Cr+" style={{ background: '#0A0E1A' }}>₹1Cr+</option>
          </select>
        </div>
      </div>

      <div>
        <label style={labelStyle}>Tell us about your project *</label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="What problem are you solving? What is your timeline? Any specific tech requirements?"
          required
          rows={4}
          style={{ ...inputStyle, resize: 'vertical', minHeight: '110px' }}
          onFocus={e => (e.target.style.borderColor = '#3B82F6')}
          onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
        />
      </div>

      {error && (
        <p style={{
          fontSize: '13px', color: '#E63946',
          background: 'rgba(230,57,70,0.08)',
          border: '1px solid rgba(230,57,70,0.2)',
          padding: '10px 14px', borderRadius: '8px',
        }}>
          ⚠️ {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="btn-primary"
        style={{
          width: '100%',
          justifyContent: 'center',
          padding: '16px',
          fontSize: '14px',
          opacity: submitting ? 0.7 : 1,
          cursor: submitting ? 'not-allowed' : 'pointer',
        }}
      >
        {submitting ? '⏳ Sending...' : '🚀  Discuss Your Project'}
      </button>

      <p style={{
        fontSize: '11px',
        color: 'rgba(255,255,255,0.25)',
        textAlign: 'center',
        lineHeight: '1.6',
      }}>
        We typically respond within 24 hours · Your information is confidential
      </p>
    </form>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Services() {

  return (
    <div className="page-wrapper" style={{ paddingTop: '80px' }}>
      <SEOHead
        title="Software Development Services — Redcubical Systems"
        description="Custom software development, staff augmentation, AI integration, and UX/UI design. We build systems that matter. Hire pre-vetted developers or outsource your entire product build to Redcubical."
        canonical="/services"
        keywords="software development company India, hire developers India, staff augmentation, AI development services, product engineering, MVP development, React Native development India"
      />

      {/* ── HERO ── */}
      <section
        style={{
          minHeight: '90vh',
          display: 'flex',
          alignItems: 'center',
          padding: '80px 32px',
          position: 'relative',
          overflow: 'hidden',
          background: `
            radial-gradient(ellipse 80% 60% at 50% -10%,
              rgba(59,130,246,0.4) 0%,
              rgba(37,99,235,0.2) 40%,
              transparent 70%),
            var(--black)
          `,
        }}
      >
        <div style={{
          position: 'absolute', top: '-200px', right: '-200px',
          width: '700px', height: '700px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,130,246,0.2) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-100px', left: '-100px',
          width: '400px', height: '400px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div style={{ maxWidth: '1280px', margin: '0 auto', width: '100%', position: 'relative' }}>
          <div className="services-hero-grid">

            {/* Left */}
            <div>
              <span className="badge" style={{ marginBottom: '32px' }}>
                💻 &nbsp; Software Engineering Services
              </span>

              <h1 className="display-heading" style={{ marginBottom: '24px' }}>
                WE BUILD<br />
                <span className="gradient-text">SYSTEMS.</span><br />
                NOT FEATURES.
              </h1>

              <p style={{
                fontSize: 'clamp(16px, 2vw, 19px)',
                color: 'rgba(255,255,255,0.65)',
                lineHeight: '1.8',
                maxWidth: '560px',
                marginBottom: '16px',
              }}>
                From MVPs to enterprise platforms. From AI integration to dedicated engineering teams.
                We bring the architectural discipline and product thinking to solve complex problems
                — not just write code.
              </p>

              <p style={{
                fontSize: '15px',
                color: 'rgba(255,255,255,0.45)',
                lineHeight: '1.7',
                maxWidth: '520px',
                marginBottom: '40px',
              }}>
                Based in Dehradun, serving clients globally. 
                Silicon Valley engineering standards at India-friendly costs.
              </p>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="btn-primary"
                >
                  Start a Project ↓
                </button>
                <button
                  onClick={() => {
                    document.getElementById('augmentation')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="btn-outline"
                >
                  Hire Developers →
                </button>
              </div>
            </div>

            {/* Right — Stats/Visual */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}>
              {[
                { value: '3+', label: 'Products Shipped', color: '#3B82F6' },
                { value: '500+', label: 'Clinics Digitised', color: '#22C55E' },
                { value: '10yr', label: 'Systems Thinking', color: '#8B5CF6' },
                { value: '48hr', label: 'Developer Onboarding', color: '#F59E0B' },
              ].map((stat, i) => (
                <div key={i} style={{
                  padding: '28px 32px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(59,130,246,0.15)',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                  transition: 'all 0.3s',
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(59,130,246,0.06)';
                    e.currentTarget.style.borderColor = 'rgba(59,130,246,0.3)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                    e.currentTarget.style.borderColor = 'rgba(59,130,246,0.15)';
                  }}
                >
                  <span style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontSize: '40px', fontWeight: '900',
                    color: stat.color, lineHeight: 1,
                  }}>
                    {stat.value}
                  </span>
                  <span style={{
                    fontSize: '14px',
                    color: 'rgba(255,255,255,0.5)',
                    fontWeight: '500',
                  }}>
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES GRID ── */}
      <section style={{ padding: '120px 32px', background: 'var(--dark)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '72px' }}>
            <span className="section-label">What We Do</span>
            <h2 className="section-title" style={{ marginBottom: '20px' }}>
              ENGINEERING SERVICES<br />
              <span className="gradient-text">FOR AMBITIOUS TEAMS</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              We take 3-4 client projects per quarter. If your problem is interesting,
              we want to solve it.
            </p>
          </div>

          <div style={{
            display: 'grid', gap: '2px',
            background: 'rgba(59,130,246,0.08)',
            border: '1px solid rgba(59,130,246,0.12)',
            borderRadius: '20px', overflow: 'hidden',
          }} className="services-grid-2">
            {services.map((service, i) => (
              <div
                key={i}
                style={{
                  padding: '48px 40px',
                  background: 'rgba(5,8,16,0.9)',
                  transition: 'all 0.3s',
                  cursor: 'pointer',
                  borderRight: i % 2 === 0 ? '1px solid rgba(59,130,246,0.08)' : 'none',
                  borderBottom: i < 2 ? '1px solid rgba(59,130,246,0.08)' : 'none',
                }}
                onMouseEnter={e => {
  e.currentTarget.style.background = 'rgba(59,130,246,0.06)';
}}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(5,8,16,0.9)';
                }}
              >
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'flex-start', marginBottom: '24px',
                }}>
                  <span style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontSize: '14px', fontWeight: '900',
                    color: service.color,
                    letterSpacing: '2px',
                  }}>
                    {service.number}
                  </span>
                  <span style={{ fontSize: '32px' }}>{service.icon}</span>
                </div>

                <h3 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '26px', fontWeight: '900',
                  color: '#fff', textTransform: 'uppercase',
                  marginBottom: '12px', lineHeight: 1.1,
                }}>
                  {service.title}
                </h3>

                <p style={{
                  fontSize: '15px',
                  color: 'rgba(255,255,255,0.5)',
                  lineHeight: '1.7',
                  marginBottom: '24px',
                }}>
                  {service.shortDesc}
                </p>

                <div style={{
                  display: 'flex', flexDirection: 'column', gap: '8px',
                }}>
                  {service.deliverables.slice(0, 3).map(d => (
                    <div key={d} style={{
                      display: 'flex', alignItems: 'center',
                      gap: '8px', fontSize: '13px',
                      color: 'rgba(255,255,255,0.4)',
                    }}>
                      <span style={{
                        width: '4px', height: '4px',
                        background: service.color,
                        borderRadius: '50%',
                      }} />
                      {d}
                    </div>
                  ))}
                  {service.deliverables.length > 3 && (
                    <span style={{
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.25)',
                      marginTop: '4px',
                    }}>
                      +{service.deliverables.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STAFF AUGMENTATION HIGHLIGHT ── */}
      <section
        id="augmentation"
        style={{
          padding: '120px 32px',
          background: 'var(--dark-2)',
          borderTop: '1px solid rgba(139,92,246,0.1)',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="augmentation-grid">
            <div>
              <span className="section-label" style={{ color: '#8B5CF6' }}>
                Staff Augmentation
              </span>
              <h2 className="section-title" style={{ marginBottom: '24px' }}>
                HIRE OUR DEVELOPERS<br />
                <span style={{
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>EMBEDDED IN YOUR TEAM</span>
              </h2>
              <p className="body-text" style={{ marginBottom: '24px' }}>
                Need to ship faster without the 3-month hiring cycle? Our engineers join your
                Slack, standups, and sprints — as if they were your own employees. You manage
                the product; we handle the execution.
              </p>
              <p className="body-text" style={{ marginBottom: '32px' }}>
                From React Native mobile developers to Python AI engineers — pre-vetted,
                senior-level, ready to start within 48 hours.
              </p>

              <div style={{ marginBottom: '32px' }}>
                {[
                  'No recruitment overhead',
                  'No long-term contracts',
                  'Direct daily communication',
                  'Scalable team size (1-10 engineers)',
                ].map((item) => (
                  <div key={item} style={{
                    display: 'flex', alignItems: 'center',
                    gap: '12px', marginBottom: '12px',
                    fontSize: '15px', color: 'rgba(255,255,255,0.6)',
                  }}>
                    <span style={{ color: '#8B5CF6', fontSize: '18px' }}>✓</span>
                    {item}
                  </div>
                ))}
              </div>

              <Link to="/contact" className="btn-blue">
                Request Developer Profiles →
              </Link>
            </div>

            <div style={{
              display: 'grid', gap: '12px',
            }}>
              {[
                { category: 'Backend', skills: 'Laravel, Django, Node, Java, Go', icon: '⚙️' },
                { category: 'Frontend', skills: 'React, Vue, Angular, Next.js', icon: '💻' },
                { category: 'Mobile', skills: 'React Native, Flutter, iOS, Android', icon: '📱' },
                { category: 'AI & Data', skills: 'LLM Integration, TensorFlow, Python', icon: '🧠' },
                { category: 'UX / Design', skills: 'Figma, UI/UX, Design Systems', icon: '🎨' },
                { category: 'DevOps', skills: 'AWS, Firebase, Docker, CI/CD', icon: '☁️' },
              ].map((dept) => (
                <div key={dept.category} style={{
                  padding: '24px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(139,92,246,0.15)',
                  borderRadius: '14px',
                  display: 'flex', alignItems: 'center',
                  gap: '16px',
                  transition: 'all 0.25s',
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(139,92,246,0.06)';
                    e.currentTarget.style.borderColor = 'rgba(139,92,246,0.3)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                    e.currentTarget.style.borderColor = 'rgba(139,92,246,0.15)';
                  }}
                >
                  <span style={{ fontSize: '28px' }}>{dept.icon}</span>
                  <div>
                    <p style={{
                      fontSize: '15px', fontWeight: '700',
                      color: '#fff', marginBottom: '4px',
                    }}>
                      {dept.category}
                    </p>
                    <p style={{
                      fontSize: '13px',
                      color: 'rgba(255,255,255,0.4)',
                    }}>
                      {dept.skills}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '72px' }}>
            <span className="section-label">How We Work</span>
            <h2 className="section-title">
              TRANSPARENT PROCESS.<br />
              <span className="gradient-text">PREDICTABLE OUTCOMES.</span>
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {processSteps.map((step, i) => (
              <div key={i} style={{
                display: 'flex', gap: '32px',
                paddingBottom: i < processSteps.length - 1 ? '48px' : '0',
                position: 'relative',
              }}>
                {/* Line */}
                {i < processSteps.length - 1 && (
                  <div style={{
                    position: 'absolute', left: '24px', top: '48px',
                    width: '2px', height: 'calc(100% - 48px)',
                    background: 'rgba(59,130,246,0.15)',
                  }} />
                )}
                
                {/* Number circle */}
                <div style={{
                  width: '48px', height: '48px',
                  borderRadius: '50%',
                  background: 'rgba(59,130,246,0.1)',
                  border: '1px solid rgba(59,130,246,0.3)',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '18px', fontWeight: '900',
                  color: '#3B82F6',
                  flexShrink: 0,
                  position: 'relative', zIndex: 2,
                }}>
                  {step.step}
                </div>

                {/* Content */}
                <div style={{ paddingTop: '8px' }}>
                  <h3 style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontSize: '24px', fontWeight: '900',
                    color: '#fff', textTransform: 'uppercase',
                    marginBottom: '10px',
                  }}>
                    {step.title}
                  </h3>
                  <p style={{
                    fontSize: '15px',
                    color: 'rgba(255,255,255,0.5)',
                    lineHeight: '1.7',
                    maxWidth: '560px',
                  }}>
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TECH STACK ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark-2)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <span className="section-label">Technologies</span>
            <h2 className="section-title" style={{ marginBottom: '16px' }}>
              RIGHT TOOL.<br />
              <span className="gradient-text">RIGHT PROBLEM.</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              We are technology-agnostic. We pick the stack that solves your
              problem best — not the one we know best.
            </p>
          </div>

          <div style={{
            display: 'grid', gap: '24px',
          }} className="tech-grid">
            {techStack.map((stack, i) => (
              <div key={i} style={{
                padding: '32px',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(59,130,246,0.1)',
                borderRadius: '16px',
              }}>
                <p style={{
                  fontSize: '11px', fontWeight: '800',
                  color: '#3B82F6', letterSpacing: '2px',
                  textTransform: 'uppercase', marginBottom: '20px',
                }}>
                  {stack.category}
                </p>
                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: '8px',
                }}>
                  {stack.items.map((item) => (
                    <span key={item} style={{
                      padding: '6px 14px',
                      background: 'rgba(59,130,246,0.08)',
                      border: '1px solid rgba(59,130,246,0.15)',
                      borderRadius: '50px',
                      fontSize: '13px',
                      color: 'rgba(255,255,255,0.7)',
                    }}>
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY REDCUBICAL ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ gap: '80px', alignItems: 'flex-start' }} className="why-us-grid">
            <div style={{ position: 'sticky', top: '100px' }}>
              <span className="section-label">Why Us</span>
              <h2 className="section-title" style={{ marginBottom: '24px' }}>
                PRODUCT DNA.<br />
                <span className="gradient-text">ENGINEERING DISCIPLINE.</span>
              </h2>
              <p className="body-text">
                We do not just write code — we build products. Our own products
                (HealHub, Suraksha, Vedant) force us to maintain the highest
                standards of security, scalability, and user experience.
                Your project benefits from the same infrastructure.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {whyUs.map((item, i) => (
                <div key={i} style={{
                  padding: '28px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(59,130,246,0.1)',
                  borderRadius: '16px',
                  transition: 'all 0.3s',
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(59,130,246,0.06)';
                    e.currentTarget.style.borderColor = 'rgba(59,130,246,0.25)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                    e.currentTarget.style.borderColor = 'rgba(59,130,246,0.1)';
                  }}
                >
                  <div style={{
                    width: '48px', height: '48px',
                    background: 'rgba(59,130,246,0.1)',
                    border: '1px solid rgba(59,130,246,0.2)',
                    borderRadius: '12px',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px', marginBottom: '16px',
                  }}>
                    {item.icon}
                  </div>
                  <h3 style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontSize: '20px', fontWeight: '900',
                    color: '#fff', textTransform: 'uppercase',
                    marginBottom: '8px',
                  }}>
                    {item.title}
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: 'rgba(255,255,255,0.5)',
                    lineHeight: '1.65',
                  }}>
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── ENGAGEMENT MODELS ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark-2)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '72px' }}>
            <span className="section-label">Flexibility</span>
            <h2 className="section-title">
              ENGAGEMENT THAT FITS<br />
              <span className="gradient-text">YOUR NEEDS</span>
            </h2>
          </div>

          <div style={{
            display: 'grid', gap: '24px',
          }} className="engagement-grid">
            {engagementModels.map((model, i) => (
              <div key={i} style={{
                padding: '40px 36px',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(59,130,246,0.12)',
                borderRadius: '20px',
                transition: 'all 0.3s',
              }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(59,130,246,0.06)';
                  e.currentTarget.style.borderColor = 'rgba(59,130,246,0.25)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                  e.currentTarget.style.borderColor = 'rgba(59,130,246,0.12)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <h3 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '26px', fontWeight: '900',
                  color: '#fff', textTransform: 'uppercase',
                  marginBottom: '12px',
                }}>
                  {model.name}
                </h3>
                <p style={{
                  fontSize: '15px',
                  color: 'rgba(255,255,255,0.5)',
                  lineHeight: '1.7',
                  marginBottom: '24px',
                }}>
                  {model.desc}
                </p>
                <div style={{
                  padding: '12px 16px',
                  background: 'rgba(59,130,246,0.08)',
                  border: '1px solid rgba(59,130,246,0.15)',
                  borderRadius: '10px',
                }}>
                  <span style={{
                    fontSize: '11px', fontWeight: '700',
                    color: 'rgba(255,255,255,0.4)',
                    textTransform: 'uppercase', letterSpacing: '1px',
                  }}>
                    Ideal for:
                  </span>
                  <p style={{
                    fontSize: '14px',
                    color: '#3B82F6',
                    fontWeight: '600',
                    marginTop: '4px',
                  }}>
                    {model.ideal}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT FORM ── */}
      <section
        id="contact"
        style={{
          padding: '120px 32px',
          background: `
            radial-gradient(ellipse 80% 60% at 50% 0%,
              rgba(59,130,246,0.12) 0%,
              transparent 70%),
            var(--black)
          `,
          borderTop: '1px solid rgba(59,130,246,0.15)',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="contact-grid">
            {/* Left */}
            <div>
              <span className="section-label">Start a Conversation</span>
              <h2 className="section-title" style={{ marginBottom: '24px' }}>
                HAVE A PROBLEM<br />
                <span className="gradient-text">WORTH SOLVING?</span>
              </h2>
              <p className="body-text" style={{ marginBottom: '32px' }}>
                We take on select projects that align with our expertise.
                Tell us what you are building. If it is a fit, we will get back
                to you within 24 hours with a proposed approach.
              </p>

              <div style={{ marginBottom: '40px' }}>
                <p style={{
                  fontSize: '13px', fontWeight: '700',
                  color: 'rgba(255,255,255,0.3)',
                  textTransform: 'uppercase', letterSpacing: '1px',
                  marginBottom: '16px',
                }}>
                  Or reach us directly
                </p>
                <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>
                  📧 a.uniyal@redcubicalsystems.com
                </p>
                <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.6)' }}>
                  📞 +91 9368487416
                </p>
              </div>

              {/* Trust badges */}
              <div style={{
                padding: '24px',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '16px',
              }}>
                <p style={{
                  fontSize: '12px', fontWeight: '700',
                  color: 'rgba(255,255,255,0.4)',
                  textTransform: 'uppercase', letterSpacing: '1px',
                  marginBottom: '12px',
                }}>
                  Recent Work
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Link to="/healhub" style={{
                    fontSize: '14px', color: '#3B82F6',
                    textDecoration: 'none',
                  }}>→ HealHub Clinic Management</Link>
                  <Link to="/suraksha" style={{
                    fontSize: '14px', color: '#3B82F6',
                    textDecoration: 'none',
                  }}>→ Suraksha Anti-Fraud App</Link>
                  <Link to="/vedant" style={{
                    fontSize: '14px', color: '#3B82F6',
                    textDecoration: 'none',
                  }}>→ Vedant AI Receptionist</Link>
                </div>
              </div>
            </div>

            {/* Right — Form */}
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(59,130,246,0.15)',
              borderRadius: '24px',
              padding: '48px 40px',
            }}>
              <ProjectForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── RESPONSIVE ── */}
      <style>{`
        .services-hero-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }
        .services-grid-2 {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
        }
        .augmentation-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: start;
        }
        .tech-grid {
          grid-template-columns: repeat(2, 1fr);
        }
        .why-us-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
        }
        .engagement-grid {
          grid-template-columns: repeat(3, 1fr);
        }
        .contact-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: start;
        }
        .services-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        @media (max-width: 1024px) {
          .services-grid-2 {
            grid-template-columns: 1fr !important;
          }
          .augmentation-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .why-us-grid {
            grid-template-columns: 1fr !important;
          }
          .engagement-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 768px) {
          .services-hero-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .tech-grid {
            grid-template-columns: 1fr !important;
          }
          .contact-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .services-form-row {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}